import React, { useEffect, useState } from 'react';
import { Mail, CheckCircle, Loader } from 'lucide-react';

export default function KreativLanding() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [nameFocused, setNameFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);

  // Anti-bot: honeypot and timing
  const [honeypot, setHoneypot] = useState('');
  const [formStartAt, setFormStartAt] = useState(Date.now());
  const [lastSubmitAt, setLastSubmitAt] = useState(0);

  useEffect(() => {
    setFormStartAt(Date.now());
  }, []);

  const isValidEmail = (value) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return re.test(String(value).toLowerCase());
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!name || !email) {
      setStatus('error');
      setMessage('Please fill in all fields.');
      return;
    }

    if (!isValidEmail(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    // Anti-bot checks
    if (honeypot && honeypot.trim().length > 0) {
      setStatus('error');
      setMessage('Submission blocked.');
      return;
    }

    const timeOnForm = Date.now() - formStartAt;
    if (timeOnForm < 2000) { // submitted too quickly
      setStatus('error');
      setMessage('Please take a moment to fill the form.');
      return;
    }

    if (lastSubmitAt && Date.now() - lastSubmitAt < 10000) { // basic rate limit
      setStatus('error');
      setMessage('Please wait a moment before submitting again.');
      return;
    }

    setStatus('loading');

    const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

    // Environment checks
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      setStatus('error');
      setMessage('Service not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
      return;
    }

    // Duplicate email check (client-side)
    try {
      const dupRes = await fetch(`${SUPABASE_URL}/rest/v1/waitlist?email=eq.${encodeURIComponent(email)}&select=id&limit=1`, {
        headers: {
          'Accept': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      });
      if (dupRes.ok) {
        const rows = await dupRes.json();
        if (Array.isArray(rows) && rows.length > 0) {
          setStatus('success');
          setMessage("You're already on the list with this email.");
          return;
        }
      } else {
        // If RLS blocks select or other error occurs, continue with insert
        console.warn('Duplicate check skipped:', dupRes.status);
      }
    } catch (dupErr) {
      console.warn('Duplicate check error:', dupErr);
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify({
          name: name,
          email: email,
          created_at: new Date().toISOString(),
        }),
      });

      setLastSubmitAt(Date.now());

      if (response.ok || response.status === 201) {
        setStatus('success');
        setMessage("🎉 You're on the list! We'll be in touch soon.");
        setEmail('');
        setName('');
        setFormStartAt(Date.now());
      } else if (response.status === 409) {
        // Unique constraint conflict — already registered
        setStatus('success');
        setMessage("You're already on the list with this email.");
      } else {
        const text = await response.text();
        console.error('Submission failed:', response.status, text);
        let displayMessage = 'Oops! Something went wrong. Please try again.';
        // Try to surface useful errors from Supabase
        try {
          const json = JSON.parse(text);
          if (json && json.message) {
            displayMessage = `Error: ${json.message}`;
          }
        } catch (_) {
          // ignore parse error
        }
        setStatus('error');
        setMessage(displayMessage);
      }
    } catch (error) {
      console.error('Network or configuration error:', error);
      setStatus('error');
      setMessage('Oops! Something went wrong. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left side - Branding */}
            <div className="bg-white p-12 flex flex-col justify-center items-center relative overflow-hidden">
              <div className="relative z-10 text-center">
                <div className="inline-block mb-5">
                  <div className="flex items-center justify-center">
                    <img
                      src="/kreativ-logo.png"
                      alt="kreatives Logo"
                      className="w-64 h-auto"
                    />
                  </div>
                </div>
                
                <div className="w-24 h-0.5 mx-auto my-5" style={{backgroundColor: '#1E22AA', opacity: 0.3}}></div>
                
                {/*<p className="text-lg text-gray-700 leading-relaxed max-w-md px-4">
                  Connect with local experts that can help your business with their knowledge and passion
                </p>*/}
                <p className="text-lg text-gray-700 leading-relaxed max-w-md px-2 py-2">
                    Whether you're a freelancer seeking your next client, a studio looking for the perfect collaborator, or a brand searching for Nordic creative talent, Kreatives is your platform.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed max-w-md px-2 py-2">
                    We're building the platform where Nordic creativity connects with the world. 
                </p>
                <p className="text-lg text-gray-700 leading-relaxed max-w-md px-2 py-2">
                    From Copenhagen to Reykjavik, from Helsinki to Oslo, we're connecting the creative minds of the Nordics to projects and people around the globe.
                </p>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="p-12 flex flex-col justify-center" style={{backgroundColor: '#1E22AA'}}>
              <div className="max-w-md mx-auto w-full">
                <h2 className="text-3xl font-bold mb-3 text-white">Join the Waitlist</h2>
                <p className="text-white/80 mb-8">
                  Be among the first to experience Kreatives. Get early access and exclusive updates.
                </p>

                <div className="space-y-5">
                  {/* Honeypot field (hidden from users) */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="company" className="block text-sm font-medium text-white/70 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      className="w-full px-4 py-3 border-2 rounded-xl"
                    />
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onKeyPress={handleKeyPress}
                      onFocus={() => setNameFocused(true)}
                      onBlur={() => setNameFocused(false)}
                      className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors text-gray-800 bg-white"
                      style={{borderColor: nameFocused ? '#FFFFFF' : 'rgba(255,255,255,0.3)'}}
                      placeholder="John Doe"
                      autoComplete="name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        onFocus={() => setEmailFocused(true)}
                        onBlur={() => setEmailFocused(false)}
                        className="w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-colors text-gray-800 bg-white"
                        style={{borderColor: emailFocused ? '#FFFFFF' : 'rgba(255,255,255,0.3)'}}
                        placeholder="john@example.com"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={status === 'loading'}
                    className="w-full font-semibold py-3 px-6 rounded-xl hover:opacity-90 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    style={{
                      backgroundColor: status === 'loading' ? '#E5E7EB' : '#FFFFFF',
                      color: status === 'loading' ? '#6B7280' : '#1E22AA'
                    }}
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" style={{color: '#6B7280'}} />
                        <span>Joining...</span>
                      </>
                    ) : (
                      <span>Join the Waitlist</span>
                    )}
                  </button>

                  {status === 'success' && (
                    <div className="p-4 bg-white/95 border-2 border-white rounded-xl text-green-700 text-sm flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>{message}</span>
                    </div>
                  )}

                  {status === 'error' && (
                    <div className="p-4 bg-white/95 border-2 border-white rounded-xl text-red-700 text-sm">
                      {message}
                    </div>
                  )}
                </div>

                <p className="text-xs text-white/60 mt-6 text-center">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>© 2025 Kreatives. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}